import React from "react";
import {
  Box,
  Typography,
  Rating,
  Paper,
  LinearProgress,
  Chip,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/system";
import { ReviewViewModel } from "../../configuration/api/apiSchema";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

interface RatingSummaryProps {
  reviews: ReviewViewModel[];
  loading: boolean;
  totalCount: number;
}

const RatingSummary: React.FC<RatingSummaryProps> = ({
  reviews,
  loading,
  totalCount,
}) => {
  // Calculate average rating
  const averageRating = reviews.length
    ? reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length
    : 0;

  // Count ratings by star
  const ratingCounts = [0, 0, 0, 0, 0]; // 5 stars, 4 stars, 3 stars, 2 stars, 1 star
  reviews.forEach((review) => {
    const rating = Math.floor(review.rating || 0);
    if (rating > 0 && rating <= 5) {
      ratingCounts[5 - rating]++;
    }
  });

  // Get most common tags
  const tagCounts: Record<string, number> = {};
  reviews.forEach((review) => {
    if (review.tags) {
      review.tags.forEach((tag) => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    }
  });

  // Sort tags by count
  const sortedTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5); // Top 5 tags

  if (loading) {
    return (
      <StyledPaper>
        <Box mb={3}>
          <Skeleton variant="text" width="60%" height={40} />
          <Box display="flex" alignItems="center" mt={1}>
            <Skeleton variant="rectangular" width={120} height={24} />
            <Skeleton variant="text" width={100} sx={{ ml: 2 }} />
          </Box>
        </Box>
        <Box mb={3}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={30} sx={{ mb: 1 }} />
          ))}
        </Box>
        <Skeleton variant="text" width="40%" height={30} />
        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" width={80} height={32} />
          ))}
        </Box>
      </StyledPaper>
    );
  }

  return (
    <StyledPaper>
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Rating Summary
        </Typography>
        <Box display="flex" alignItems="center">
          <Rating
            value={averageRating}
            readOnly
            precision={0.1}
            size="large"
          />
          <Typography variant="h6" sx={{ ml: 2 }}>
            {averageRating.toFixed(1)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({totalCount} {totalCount === 1 ? "review" : "reviews"})
          </Typography>
        </Box>
      </Box>

      <Box mb={3}>
        {[5, 4, 3, 2, 1].map((star, index) => {
          const count = ratingCounts[index];
          const percentage = totalCount ? (count / totalCount) * 100 : 0;
          
          return (
            <Box key={star} display="flex" alignItems="center" mb={1}>
              <Typography variant="body2" sx={{ width: 60 }}>
                {star} {star === 1 ? "star" : "stars"}
              </Typography>
              <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: star >= 4 ? "#4caf50" : star >= 3 ? "#ff9800" : "#f44336",
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 40 }}>
                {count}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {sortedTags.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Most Mentioned
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {sortedTags.map(([tag, count]) => (
              <Chip
                key={tag}
                label={`${tag} (${count})`}
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        </>
      )}
    </StyledPaper>
  );
};

export default RatingSummary; 